<script setup>
import PresenceService from "@/api/services/newPresencia.service";
import PresenceCheckChanges from "@/components/shared/Presence/PresenceCheckChanges.vue";
import { computed, onMounted, ref, watch } from 'vue';
import { useUiStore } from "@/app/stores/uiStore";
import notification from "@/utils/plugins/NotificationService";
import { workRecordRequestApiError } from "@/utils/presenceAbsence";

const uiStore = useUiStore();
// eslint-disable-next-line no-undef
const props = defineProps({
    isAccessedAdmin: Boolean,
    presesenceChanges: Array,
});
// eslint-disable-next-line no-undef
const emit = defineEmits(['close', 'finish']);

const isAccessedAdmin = computed(() => {
    return props.isAccessedAdmin
})
const presesenceChanges = computed(() => {
    return props.presesenceChanges;
})

const isApprovedOrRejected = computed(() => {
    if (presesenceChanges.value.length <= 0) return true;
    if (!presesenceChanges.value[0].requests) return true
    if (presesenceChanges.value[0].requests.workRecordRequests.length < 0) return true;
    const status = presesenceChanges.value[0].requests.workRecordRequests[0].status;
    return status === 'rejected' || status === 'approved';
})

const finishedCheckChanges = ref(false);

onMounted(() => {
})

const clickedCheckChanges = async () => {
    if (presesenceChanges.value.length <= 0) return;
    uiStore.startLoading();

    const items = [];
    for (const element of presesenceChanges.value) {
        const action = element.isAccepted ? 'approve' : 'reject';
        for (const req of element.requests.workRecordRequests) {
            items.push({ id: req.id, action });
        }
    }

    if (items.length === 0) {
        uiStore.stopLoading();
        return;
    }

    try {
        await PresenceService.batchResolveWorkRecordsRequestAdmin({ items });
        finishedCheckChanges.value = true;
    } catch (error) {
        // No se cierra el diálogo: nada se ha aprobado (ver nota de atomicidad en el backend),
        // así que el admin necesita ver el aviso y poder corregir su selección sin perderla.
        // El mensaje ya trae el tipo y la fecha/hora de cada solicitud bloqueada (details.items).
        const { message } = workRecordRequestApiError(error);
        notification.addNotification({ type: 'error', message, duration: 8000 });
    } finally {
        uiStore.stopLoading();
    }
}

const closeSeeChanges = () => {
    emit('close');
}

watch(finishedCheckChanges, (newVal) => {
    if (newVal) {
        finishedCheckChanges.value = false;
        emit('finish')
    }
})

</script>

<template>
    <div>
        <div class="my-header">
            <svg id="asterisk" xmlns="http://www.w3.org/2000/svg" width="700" height="1024" viewBox="0 0 724 1024">
                <path
                    d="M0 499.8l59-181.7 332.5 108.6-59 180.8-332.5-107.7zM79.6 744.3l206.1-282.9 154.5 111.5-206.1 283.8-154.5-112.4zM266.9 167.3h191.1v350.3h-191.1v-350.3zM285.6 572.9l154.5-111.5 206.1 282.9-154.5 112.4-206.1-283.8zM332.5 426.8l332.5-108.6 59 181.7-331.6 107.7-59.9-180.8z" />
            </svg>
            <span class="text-header ml-5"> Presencia</span>
            <img class="close-icon pointer" src="@/assets/images/aspa_blue_small_entrada.png" alt=""
                @click="closeSeeChanges()">
        </div>
        <div class="subtitle-header">
            <svg id="chronometer" version="1.1" xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 233.766 233.766"
                style="enable-background: new 0 0 233.766 233.766" xml:space="preserve">
                <g>
                    <path
                        d="M188.169,79.423l6.059-6.058l2.906,2.906c1.465,1.465,3.384,2.197,5.304,2.197c1.919,0,3.839-0.732,5.303-2.196
                              c2.93-2.929,2.93-7.678,0.001-10.606l-16.419-16.421c-2.929-2.93-7.678-2.929-10.607-0.001c-2.93,2.929-2.93,7.678-0.001,10.607
                              l2.907,2.907l-6.06,6.059c-10.441-8.901-22.855-15.553-36.484-19.201c4.082-5.216,6.524-11.773,6.524-18.895
                              C147.602,13.781,133.821,0,116.883,0c-16.939,0-30.72,13.781-30.72,30.721c0,7.121,2.442,13.679,6.525,18.895
                              c-13.63,3.647-26.043,10.3-36.485,19.201l-6.059-6.059l2.907-2.907c2.929-2.929,2.928-7.678-0.001-10.607
                              c-2.93-2.928-7.678-2.929-10.607,0.001L26.024,65.666c-2.929,2.929-2.928,7.678,0,10.606c1.465,1.464,3.384,2.196,5.303,2.196
                              s3.839-0.732,5.304-2.197l2.906-2.906l6.058,6.058c-13.944,16.357-22.375,37.551-22.375,60.68
                              c0,51.646,42.017,93.663,93.662,93.663s93.662-42.017,93.662-93.663C210.545,116.974,202.113,95.78,188.169,79.423z
                              M101.163,30.721c0-8.668,7.052-15.721,15.72-15.721s15.72,7.053,15.72,15.721s-7.052,15.72-15.72,15.72
                              S101.163,39.389,101.163,30.721z M116.883,218.766c-43.375,0-78.662-35.288-78.662-78.663c0-43.374,35.288-78.662,78.662-78.662
                              c43.374,0,78.662,35.288,78.662,78.662C195.545,183.477,160.257,218.766,116.883,218.766z" />
                    <path d="M116.883,76.981c-4.142,0-7.5,3.357-7.5,7.5v55.621c0,4.143,3.358,7.5,7.5,7.5c4.143,0,7.5-3.357,7.5-7.5V84.481
                              C124.383,80.339,121.025,76.981,116.883,76.981z" />
                </g>
            </svg>
            <span class="header-subtitle">{{ isAccessedAdmin ? 'Revisar Cambios Solicitados' : 'Cambios Pendientes'
                }}</span>
        </div>
        <div v-for="item in presesenceChanges" :key="item.day" class="mb-2">
            <PresenceCheckChanges :day="item.day" v-model="item.isAccepted" :requests="item.requests"
                :admin="isAccessedAdmin" />
        </div>
        <div v-if="presesenceChanges.length <= 0">
            <h2 class="text-center">No hay solicitudes para revisar</h2>
        </div>
        <div>
            <ul class="legend">
                <li><span class="legend-circle green"></span> Nuevo</li>
                <li><span class="legend-circle yellow"></span> Modificado</li>
                <li><span class="legend-circle red"></span> Cancelado</li>
            </ul>
        </div>
        <div class="d-flex justify-content-end gap-2" v-if="!isApprovedOrRejected">
            <button class="btn btn_yellow px-4 mt-3" style="width: 250px;" @click="closeSeeChanges"
                v-if="presesenceChanges.length > 0 && isAccessedAdmin">
                Cerrar
            </button>
            <button class="btn btn_red px-4 mt-3" style="width: 250px;" @click="clickedCheckChanges"
                v-if="presesenceChanges.length > 0 && isAccessedAdmin">
                Aceptar
            </button>
        </div>
    </div>

</template>

<style scoped lang="scss">
.my-header {
    margin-top: 2px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
}

.text-header {
    text-align: left;
    font-size: 4em;
    font-weight: 900;
    letter-spacing: -0.05em;
    color: #f9156f;
    margin-left: 30px;
}

.header-subtitle {
    font-weight: 800;
    font-size: 40px;
    margin-left: 10px;
    color: $font-blue-color;
}

.subtitle-header {
    #chronometer {
        fill: $bg-blue;
        width: 2.9em;
        padding-bottom: 21px;
    }
}

.legend {
    list-style: none;
    display: flex;
    gap: 1rem;
    /* espacio entre elementos */
    padding: 0;
    margin: 0;
    margin-left: 10px;
    align-items: center;
}

.legend li {
    display: flex;
    align-items: center;
    font-size: 0.9rem;
    color: #333;
}

.legend-circle {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    display: inline-block;
    margin-right: 6px;
}

.legend-circle.green {
    background-color: #99d68a;
}

.legend-circle.yellow {
    background-color: #e7e558;
}

.legend-circle.red {
    background-color: #ff0000;
}

@media (max-width: 625px) {
    .subtitle-header {
        #chronometer {
            width: 2em;
            padding-bottom: 10px;
        }

        .header-subtitle {
            font-size: 23px;
        }
    }    
}
</style>
